import React, { useEffect } from 'react';
import { Spinner, Box, Link } from '@amzn/awsui-components-console';
import { FormProvider, useForm } from 'react-hook-form';
import { GetApplicationResponse, UpdateApplicationResponse } from '@amzn/snow-data-service-typescript-client';

import { SDS_APIGATEWAY_ENDPOINT } from 'src/config';
import { useAuthContext } from 'src/providers/AuthContext';
import { logger } from 'src/utils/logger';
import { useFetchForSDS } from 'src/hooks/useFetchForSDS';
import useFetchOnUserActionUsingSDS from '../../../../hooks/useFetchOnUserActionUsingSDS';
import { useNavigation } from '../../navigation/hooks/useNavigation';
import { ROUTES } from '../../navigation/types/navigationTypes';
import { useStore } from 'src/stores/store';

import EditApplicationForm from './EditApplicationPageForm';
import { ApplicationProfileFormMetadata, convertToApplicationProfileData, DEFAULT_APPLICATION_DATA, convertFromApplicationProfileData } from '../AddApplication/ApplicationFormGenerationUtils';

interface EditApplicationPageProps {
	onCancel: () => void;
	applicationId: string;
	applicationName: string | null;
}

const EditApplicationPage: React.FC<EditApplicationPageProps> = ({
	onCancel,
	applicationId,
	applicationName,
}) => {
	const methods = useForm<ApplicationProfileFormMetadata>({
		defaultValues: DEFAULT_APPLICATION_DATA,
	});

	const {
		result: { isLoading: isUpdating, data: updateData, error: updateError, flashBarItems: updateFlashBarItems },
		onClickCB: updateApp,
	} = useFetchOnUserActionUsingSDS<UpdateApplicationResponse>();
	const { userName } = useAuthContext();
	const { navigate } = useNavigation();
	const { addNavigationError } = useStore();

	const {
		isLoading: isLoadingApp,
		data: appData,
		error: appError,
	} = useFetchForSDS<GetApplicationResponse>({
		url: SDS_APIGATEWAY_ENDPOINT,
		path: applicationId ? `/application/${applicationId}` : '',
		method: 'GET',
	});

	useEffect(() => {
		if (appData?.applicationProfileMetadata) {
			const formData = convertFromApplicationProfileData(
				appData.applicationProfileMetadata
			);

			methods.reset(formData);
		}
	}, [appData, methods]);

	useEffect(() => {
		if (appError) {
			addNavigationError({
				message: `Failed to load application for editing: ${appError}`,
				type: 'error',
				timestamp: Date.now(),
			});
		}

		if (updateError) {
			logger.error(`Error updating application: ${updateError}`);

			addNavigationError({
				message: `Failed to update application: ${updateError}`,
				type: 'error',
				timestamp: Date.now(),
			});
		}
	}, [appError, updateError, addNavigationError]);

	useEffect(() => {
		if (updateData?.applicationId) {
			addNavigationError({
				message: (
					<>
						Application{' '}
						<Link
							variant="primary"
							onFollow={() =>
								navigate(
									ROUTES.APPLICATION_DETAILS,
									`/applicationManagement/${applicationId}`
								)
							}
						>
							{appData?.applicationProfileMetadata?.applicationName}{' '}
						</Link>{' '}
						updated successfully!
					</>
				),
				type: 'success',
				timestamp: Date.now(),
			});

			navigate(
				ROUTES.APPLICATION_DETAILS,
				`/applicationManagement/${applicationId}`
			);
		}
	}, [updateData, applicationId, appData?.applicationProfileMetadata?.applicationName, navigate, addNavigationError]);

	const submitForm = async (data: ApplicationProfileFormMetadata) => {
		if (!applicationId) {
			logger.error('Cannot update application: applicationId is null');

			return;
		}

		const applicationProfileData = convertToApplicationProfileData(
			data,
			userName
		);

		await updateApp({
			path: `/application/${applicationId}`,
			method: 'PUT',
			body: { applicationProfileData },
			url: SDS_APIGATEWAY_ENDPOINT,
		});
	};

	return isLoadingApp ? (
		<Box textAlign="center" padding="l">
			<Spinner size="large" />
		</Box>
	) : appData?.applicationProfileMetadata ? (
		<FormProvider {...methods}>
			<EditApplicationForm
				onSubmit={submitForm}
				onCancel={onCancel}
				isLoading={isUpdating}
				flashBarItems={updateFlashBarItems}
				error={updateError}
				applicationName={applicationName}
			/>
		</FormProvider>
	) : null;
};

export default EditApplicationPage;
